// Utility functions for cleaning up category names in charts

/**
 * Clean category names by removing common prefixes
 * @param {string} categoryName - The raw category name
 * @returns {string} - The cleaned category name
 */
export const cleanCategoryName = (categoryName) => {
  if (!categoryName || typeof categoryName !== 'string') {
    return 'Unknown';
  }
  
  let cleanedName = categoryName;
  
  // Remove "Succession Planning   " prefix (with spaces)
  cleanedName = cleanedName.replace(/^Succession Planning\s+/, '');
  
  // Remove "Practice Exchange   " prefix (with spaces)
  cleanedName = cleanedName.replace(/^Practice Exchange\s+/, '');
  
  // Remove "Succession Planning - " prefix (with dash)
  cleanedName = cleanedName.replace(/^Succession Planning\s*-\s*/, '');
  
  // Remove "Practice Exchange - " prefix (with dash)
  cleanedName = cleanedName.replace(/^Practice Exchange\s*-\s*/, '');
  
  // Return cleaned name or fallback to original
  return cleanedName.trim() || categoryName;
};

/**
 * Clean activity type names by removing common prefixes
 * @param {string} activityTypeName - The raw activity type name
 * @returns {string} - The cleaned activity type name
 */
export const cleanActivityTypeName = (activityTypeName) => {
  if (!activityTypeName || typeof activityTypeName !== 'string') {
    return 'Unknown';
  }
  
  let cleanedName = activityTypeName;
  
  // Remove common activity type prefixes
  cleanedName = cleanedName.replace(/^Client\s+/, '');
  cleanedName = cleanedName.replace(/^Phone\s+/, '');
  cleanedName = cleanedName.replace(/^Email\s+/, '');
  
  // Return cleaned name or fallback to original
  return cleanedName.trim() || activityTypeName;
};

/**
 * Clean category name and detect which prefix was removed
 * @param {string} categoryName - The raw category name
 * @returns {Object} - { cleanedName, prefix }
 */
export const cleanCategoryNameWithPrefix = (categoryName) => {
  if (!categoryName || typeof categoryName !== 'string') {
    return { cleanedName: 'Unknown', prefix: null };
  }
  
  let cleanedName = categoryName;
  let prefix = null;
  
  // Check for "Succession Planning" prefixes
  if (categoryName.match(/^Succession Planning\s+/)) {
    cleanedName = categoryName.replace(/^Succession Planning\s+/, '');
    prefix = 'SP';
  } else if (categoryName.match(/^Succession Planning\s*-\s*/)) {
    cleanedName = categoryName.replace(/^Succession Planning\s*-\s*/, '');
    prefix = 'SP';
  }
  // Check for "Practice Exchange" prefixes
  else if (categoryName.match(/^Practice Exchange\s+/)) {
    cleanedName = categoryName.replace(/^Practice Exchange\s+/, '');
    prefix = 'PE';
  } else if (categoryName.match(/^Practice Exchange\s*-\s*/)) {
    cleanedName = categoryName.replace(/^Practice Exchange\s*-\s*/, '');
    prefix = 'PE';
  }
  
  return { 
    cleanedName: cleanedName.trim() || categoryName, 
    prefix 
  };
};

/**
 * Clean activity type name and detect which prefix was removed
 * @param {string} activityTypeName - The raw activity type name
 * @returns {Object} - { cleanedName, prefix }
 */
export const cleanActivityTypeNameWithPrefix = (activityTypeName) => {
  if (!activityTypeName || typeof activityTypeName !== 'string') {
    return { cleanedName: 'Unknown', prefix: null };
  }
  
  let cleanedName = activityTypeName;
  let prefix = null;
  
  // Check for common activity type prefixes
  if (activityTypeName.match(/^Client\s+/)) {
    cleanedName = activityTypeName.replace(/^Client\s+/, '');
    prefix = 'Client';
  } else if (activityTypeName.match(/^Phone\s+/)) {
    cleanedName = activityTypeName.replace(/^Phone\s+/, '');
    prefix = 'Phone';
  } else if (activityTypeName.match(/^Email\s+/)) {
    cleanedName = activityTypeName.replace(/^Email\s+/, '');
    prefix = 'Email';
  }
  
  return { 
    cleanedName: cleanedName.trim() || activityTypeName, 
    prefix 
  };
};

/**
 * Clean stack key based on stack dimension
 * @param {string} stackKey - The raw stack key
 * @param {string} stackDimension - The stack dimension (CATEGORY, ACTIVITY_TYPE, etc.)
 * @returns {Object} - { cleanedName, prefix }
 */
export const cleanStackKeyWithPrefix = (stackKey, stackDimension) => {
  if (stackDimension === 'ACTIVITY_TYPE') {
    return cleanActivityTypeNameWithPrefix(stackKey);
  } else if (stackDimension === 'CATEGORY') {
    return cleanCategoryNameWithPrefix(stackKey);
  } else {
    // For other stack dimensions (like PLANNED), return as-is
    return { cleanedName: stackKey, prefix: null };
  }
};

/**
 * Clean chart data buckets by applying category name cleaning to keys
 * @param {Array} buckets - Array of bucket objects with key property
 * @returns {Array} - Array of buckets with cleaned keys
 */
export const cleanChartDataBuckets = (buckets) => {
  if (!Array.isArray(buckets)) {
    return buckets;
  }
  
  return buckets.map(bucket => ({
    ...bucket,
    key: cleanCategoryName(bucket.key)
  }));
};

/**
 * Clean aggregated chart data by applying category name cleaning
 * @param {Array} aggregates - Array of aggregate objects with buckets
 * @returns {Array} - Array of aggregates with cleaned bucket keys
 */
export const cleanAggregatedChartData = (aggregates) => {
  if (!Array.isArray(aggregates)) {
    return aggregates;
  }
  
  return aggregates.map(aggregate => ({
    ...aggregate,
    buckets: cleanChartDataBuckets(aggregate.buckets)
  }));
};

/**
 * Clean chart data while preserving unique color assignments
 * This approach cleans the display names but ensures unique colors based on original names
 * @param {Array} aggregates - Array of aggregate objects with buckets  
 * @param {string} stackDimension - The stack dimension
 * @param {Function} getStackColor - Function to get color for a stack key
 * @returns {Object} - { cleanedAggregates, colorMap }
 */
export const cleanAggregatedChartDataWithColors = (aggregates, stackDimension, getStackColor) => {
  if (!Array.isArray(aggregates)) {
    return { cleanedAggregates: aggregates, colorMap: {}, originalKeyMap: {} };
  }
  
  const colorMap = {};
  const originalKeyMap = {}; // Map from display key back to original key
  const cleanedNameTracker = {}; // Track cleaned names and their prefixes
  
  // Process each aggregate to clean names and assign unique colors
  const cleanedAggregates = aggregates.map(aggregate => ({
    ...aggregate,
    buckets: Array.isArray(aggregate.buckets) ? aggregate.buckets.map(bucket => {
      const originalKey = bucket.key;
      const { cleanedName, prefix } = cleanStackKeyWithPrefix(originalKey, stackDimension);
      
      // Create a unique display key
      let displayKey = cleanedName;
      
      // If we've seen this cleaned name before with a different prefix, make it unique
      if (cleanedNameTracker[cleanedName]) {
        const existingPrefixes = cleanedNameTracker[cleanedName];
        
        if (!existingPrefixes.includes(prefix)) {
          // Different prefix for same cleaned name - add small suffix
          displayKey = prefix ? `${cleanedName} (${prefix})` : cleanedName;
          existingPrefixes.push(prefix);
        } else {
          // Same prefix and cleaned name - this is truly the same category
          displayKey = prefix && existingPrefixes.length > 1 ? `${cleanedName} (${prefix})` : cleanedName;
        }
      } else {
        // First time seeing this cleaned name
        cleanedNameTracker[cleanedName] = [prefix];
        
        // Only add prefix suffix if there might be conflicts (check if other buckets in same data set have same cleaned name)
        const allBuckets = aggregates.flatMap(agg => agg.buckets || []);
        const sameCleaned = allBuckets.filter(b => {
          const { cleanedName: otherCleaned } = cleanStackKeyWithPrefix(b.key, stackDimension);
          return otherCleaned === cleanedName;
        });
        
        if (sameCleaned.length > 1 && prefix) {
          displayKey = `${cleanedName} (${prefix})`;
        }
      }
      
      // Assign color based on the original key to ensure uniqueness
      colorMap[displayKey] = getStackColor(stackDimension, originalKey);
      
      // Map display key back to original key for drill-down purposes
      // Use the original key as-is since it matches the database format
      originalKeyMap[displayKey] = originalKey;
      
      return {
        ...bucket,
        key: displayKey
      };
    }) : aggregate.buckets
  }));
  
  return { cleanedAggregates, colorMap, originalKeyMap };
};
